import { supabase } from './supabase';
import { DEFAULT_PARTNERS } from './vars';

/**
 * v1.7.40.89 -- real fix. `partners.active` already existed but this
 * function ignored it entirely, so an inactive partner still appeared in
 * the sidebar switcher and every picker fed by AppContext. Confirmed
 * live: Travel In Faith is active=false and was still showing.
 *
 * Deliberately defaults to active-only rather than adding a filter at
 * each call site -- the switcher, dashboard rollups, and default views
 * all read from here, and they should all respect the toggle.
 *
 * Black Hole Rule: this HIDES, it never deletes. Nothing about an
 * inactive partner's data is removed or unlinked, and passing
 * includeInactive:true brings them back for admin surfaces (Partner
 * Profile, Settings) that genuinely need to see and re-toggle them.
 */
export async function fetchPartners({ includeInactive = false } = {}) {
  let q = supabase.from('partners').select('*');
  if (!includeInactive) q = q.eq('active', true);
  const { data, error } = await q.order('sort_order');
  if (error) throw error;
  return data;
}

export async function seedPartners() {
  const { count } = await supabase
    .from('partners')
    .select('*', { count: 'exact', head: true });
  if (count && count > 0) return;

  const rows = DEFAULT_PARTNERS.map(({ id, ...rest }) => rest);
  const { error } = await supabase.from('partners').insert(rows);
  if (error) console.error('Seed error:', error);
}

const ONBOARD_FIELDS = [
  'email', 'brand_color', 'secondary_color', 'brand_font', 'logo_url',
  'tagline', 'website', 'brand_philosophy', 'website_summary',
  'email_signature', 'linkedin_url', 'phone',
];

export function onboardScore(partner) {
  if (!partner) return 0;
  const filled = ONBOARD_FIELDS.filter(
    (f) => partner[f] !== null && partner[f] !== undefined && partner[f] !== ''
  ).length;
  return Math.round((filled / ONBOARD_FIELDS.length) * 100);
}

export async function savePartner(partner) {
  const { data, error } = await supabase
    .from('partners')
    .upsert(partner)
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function fetchWebsiteSummary(partner) {
  if (!partner.website) throw new Error('No website URL set for this partner.');

  const prompt = `You are a B2B sales intelligence assistant. Visit the website at ${partner.website} and write a concise 3–5 sentence business summary covering: what the company does, their core products or services, target market, and any notable differentiators. Be factual and direct.`;

  const res = await fetch('https://api.anthropic.com/v1/messages', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      model: 'claude-sonnet-4-20250514',
      max_tokens: 1000,
      tools: [{ type: 'web_search_20250305', name: 'web_search' }],
      messages: [{ role: 'user', content: prompt }],
    }),
  });

  const data = await res.json();
  const text = data.content
    ?.filter((b) => b.type === 'text')
    .map((b) => b.text)
    .join('\n')
    .trim();

  if (!text) throw new Error('No summary returned.');

  const { error } = await supabase
    .from('partners')
    .update({ website_summary: text, website_summary_fetched_at: new Date().toISOString() })
    .eq('id', partner.id);
  if (error) throw error;

  return text;
}
